import React, { useState, useEffect } from 'react';
import { Box, Tooltip, IconButton, Badge } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterPanel from './FilterPanel'; // Correct path within controls/
import { mapConfig } from '../../../config/mapConfig'; // Corrected path
// Import authenticated API services
import { organizationsApi, classificationApi, default as api } from '../../../services/api';
// Import user context for auto-selection
import { useAuth } from '../../../context/AuthContext';

const FilterManager = ({
  // Props from parent (MapViewPage)
  onFilterChange, // Callback to pass selected filters up
  assets, // Receive the filtered assets array
  displayedCount, // Current number of displayed assets
  // Layer visibility props (passed down to FilterPanel)
  showRoadLines,
  setShowRoadLines,
  showIoTSensors,
  setShowIoTSensors,
  showSgTebuLayers,
  setShowSgTebuLayers,
  showAssetFootprints,
  setShowAssetFootprints
  // Removed 3D model props
}) => {
  const theme = useTheme();
  // Get user context for auto-selection and discipline filtering
  const { user, isAuthenticated, isLoading, hasDiscipline } = useAuth();

  // Fix user data access - remove double nesting
  const isSystemAdmin = user?.role === 'admin' && user?.organizationId === null;
  const userOrganizationId = user?.organizationId;

  // State variables
  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
  const [organizationsList, setOrganizationsList] = useState([]);
  // Store all networks/sections for the selected organization
  const [_allNetworksForOrg, set_allNetworksForOrg] = useState([]);
  const [_allSectionsForOrg, set_allSectionsForOrg] = useState([]);
  // Lists for display in dropdowns
  const [displayableNetworks, setDisplayableNetworks] = useState([]);
  const [displayableRegions, setDisplayableRegions] = useState([]);
  const [displayableSections, setDisplayableSections] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [typesList, setTypesList] = useState([]);
  const [subtypesList, setSubtypesList] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedNetworkId, setSelectedNetworkId] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [selectedSubtypeId, setSelectedSubtypeId] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [totalAssetCount, setTotalAssetCount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Handlers
  const toggleFilterPanel = () => {
    setIsFilterPanelVisible(prev => !prev);
  };

  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganizationId(organizationId);
    // When organization changes, reset both network and section
    setSelectedNetworkId('');
    setSelectedSectionId('');
    setSelectedRegion('');
  };
  const handleNetworkChange = (networkId) => {
    setSelectedNetworkId(networkId);
    // When network changes, reset the section
    setSelectedSectionId('');
    // Don't reset region anymore
  };
  const handleSectionChange = (id) => {
    setSelectedSectionId(id);
    // No children to reset
  };
  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    // When category changes, reset the type and subtype
    setSelectedTypeId('');
    setSelectedSubtypeId('');
  };
  const handleTypeChange = (typeId) => {
    console.log("FilterManager: Type filter selected - ID:", typeId);
    setSelectedTypeId(typeId);
    // When type changes, reset the subtype
    setSelectedSubtypeId('');
  };
  const handleSubtypeChange = (id) => {
    setSelectedSubtypeId(id);
    // No children to reset
  };
  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    // Don't reset network and section anymore
  };
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    // Update combined filters in parent component
    onFilterChange({
      ...collectCurrentFilters(),
      status: value
    });
  };
  const handleResetFilters = () => {
    // Reset all filters except organization for non-admin users
    if (!isSystemAdmin && userOrganizationId) {
      setSelectedOrganizationId(userOrganizationId); // Keep organization selected for non-admin
    } else {
      setSelectedOrganizationId(''); // Allow clearing for admin
    }
    
    setSelectedNetworkId('');
    setSelectedSectionId('');
    setSelectedCategoryId('');
    setSelectedTypeId('');
    setSelectedSubtypeId('');
    setSelectedRegion('');
    setSelectedStatus('');
    setSearchTerm('');
    
    // Send reset filters to parent
    onFilterChange({
      organization_id: !isSystemAdmin && userOrganizationId ? userOrganizationId : '',
      network_id: '',
      section_id: '',
      category_id: '',
      type_id: '',
      subtype_id: '',
      searchTerm: '',
      region: '',
      status: ''
    });
  };

  // Effect to call onFilterChange when any selected filter or search term changes
  useEffect(() => {
    if (typeof onFilterChange === 'function') {
      // Pass only selected filter values and search term
      onFilterChange({
        organization_id: selectedOrganizationId || '',
        network_id: selectedNetworkId || '',
        section_id: selectedSectionId || '',
        category_id: selectedCategoryId || '',
        type_id: selectedTypeId || '',
        subtype_id: selectedSubtypeId || '',
        region: selectedRegion || '',
        searchTerm: searchTerm || '',
        status: selectedStatus || ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOrganizationId, 
    selectedNetworkId, 
    selectedSectionId,
    selectedRegion,
    selectedCategoryId, 
    selectedTypeId,
    selectedSubtypeId, 
    searchTerm,
    selectedStatus
  ]);

// Fetch initial filter options on mount
useEffect(() => {
  // Use authenticated API calls for each endpoint
  const fetchInitialData = async () => {
    try {
      // Fetch organizations using authenticated API
      const orgResponse = await organizationsApi.getAll();
      setOrganizationsList(orgResponse.data);
      
      // Auto-select user's organization for non-admin users
      if (!isSystemAdmin && userOrganizationId && orgResponse.data.length > 0) {
        console.log('Auto-selection check - isSystemAdmin:', isSystemAdmin);
        console.log('Auto-selection check - userOrganization:', userOrganizationId);
        console.log('Auto-selection check - organizations fetched:', orgResponse.data);
        
        const userOrg = orgResponse.data.find(org => org.id === userOrganizationId);
        console.log('Auto-selection check - found userOrg:', userOrg);
        
        if (userOrg) {
          console.log('Auto-selecting organization:', userOrg.name, 'ID:', userOrganizationId);
          setSelectedOrganizationId(userOrganizationId);
          
          // For network/section users, also auto-select their accessible networks
          if (user?.permissionScope === 'network_section') {
            try {
              console.log('Network/section user detected - fetching user permissions...');
              const permissionsResponse = await api.get(`/auth/me`);
              const userPermissions = permissionsResponse.data.permissions || [];
              
              console.log('User permissions fetched:', userPermissions);
              
              // Look for network-level permissions (these give access to entire networks)
              const networkPermissions = userPermissions.filter(p => p.network_id && !p.section_id);
              
              if (networkPermissions.length > 0) {
                // Auto-select the first accessible network
                const firstNetworkId = networkPermissions[0].network_id;
                console.log('Auto-selecting first accessible network:', firstNetworkId);
                setSelectedNetworkId(firstNetworkId);
              } else {
                // Look for section-level permissions and auto-select the first network that contains accessible sections
                const sectionPermissions = userPermissions.filter(p => p.section_id);
                if (sectionPermissions.length > 0) {
                  // Get unique network IDs from section permissions
                  const networkIds = [...new Set(sectionPermissions.map(p => p.network_id).filter(Boolean))];
                  if (networkIds.length > 0) {
                    console.log('Auto-selecting network from section permissions:', networkIds[0]);
                    setSelectedNetworkId(networkIds[0]);
                  }
                }
              }
            } catch (permError) {
              console.error('Error fetching user permissions for auto-selection:', permError);
            }
          }
        } else {
          console.log('ERROR: User organization not found in organizations list');
          console.log('Looking for organization ID:', userOrganizationId);
          console.log('Available organizations:', orgResponse.data.map(org => ({ id: org.id, name: org.name })));
        }
      } else {
        console.log('Auto-selection skipped - isSystemAdmin:', isSystemAdmin, 'userOrganization:', userOrganizationId, 'orgs length:', orgResponse.data.length);
      }
      
      // Fetch categories using authenticated API and filter by user's disciplines
      const categoriesResponse = await classificationApi.getCategories();
      const allCategories = categoriesResponse.data;

      // Filter categories by user's disciplines (unless system admin)
      const accessibleCategories = isSystemAdmin
        ? allCategories
        : allCategories.filter(cat => hasDiscipline(cat.name));

      console.log('FilterManager: Categories filtered by discipline:', {
        total: allCategories.length,
        accessible: accessibleCategories.length,
        isSystemAdmin
      });

      setCategoriesList(accessibleCategories);
      
      // Fetch types using authenticated API
      const typesResponse = await classificationApi.getTypes();
      setTypesList(typesResponse.data);
      
      // Fetch subtypes using authenticated API
      const subtypesResponse = await classificationApi.getSubtypes();
      setSubtypesList(subtypesResponse.data);
    } catch (error) {
      console.error('Error fetching initial filter data:', error);
      // Set empty arrays on error
      setOrganizationsList([]);
      setCategoriesList([]);
      setTypesList([]);
      setSubtypesList([]);
    }
  };
  
  fetchInitialData();
  
  const fetchTotalCount = async () => {
    try {
      // Use the imported authenticated api instance for custom endpoints
      const response = await api.get('/assets/count');
      setTotalAssetCount(response.data.count);
    } catch (error) {
      console.error("Error fetching total asset count:", error);
      setTotalAssetCount(null);
    }
  };
  
  fetchTotalCount();
}, [isSystemAdmin, userOrganizationId, user?.permissionScope, hasDiscipline]); // Add hasDiscipline to dependencies for discipline filtering

// Fetch Networks for the selected Organization
useEffect(() => {
  if (selectedOrganizationId) {
    const fetchNetworks = async () => {
      try {
        // Use the imported authenticated api instance
        const response = await api.get(`/networks?organization_id=${selectedOrganizationId}`);
        set_allNetworksForOrg(response.data);
      } catch (error) {
        console.error('Error fetching networks:', error);
        set_allNetworksForOrg([]);
      }
    };
    fetchNetworks();
  } else {
    set_allNetworksForOrg([]); // Clear if no organization is selected
  }
  // Reset dependent filters
  setSelectedNetworkId('');
  setSelectedSectionId('');
}, [selectedOrganizationId]);

// Fetch all Sections for the networks of the selected Organization
useEffect(() => {
  if (selectedOrganizationId) {
    const fetchAllSectionsForOrg = async () => {
      try {
        // Use the imported authenticated api instance
        const response = await api.get(`/sections?organization_id=${selectedOrganizationId}`);
        set_allSectionsForOrg(response.data || []);
      } catch (error) {
        console.error("Error fetching sections for organization:", error);
        set_allSectionsForOrg([]);
      }
    };
    fetchAllSectionsForOrg();
  } else {
    set_allSectionsForOrg([]); // Clear if no org selected
  }
  setSelectedSectionId(''); // Reset section when org's sections change
}, [selectedOrganizationId]); // Remove _allNetworksForOrg dependency

// Derive displayableRegions from _allSectionsForOrg
useEffect(() => {
  if (_allSectionsForOrg.length > 0) {
    const regions = [...new Set(_allSectionsForOrg.map(section => section.region).filter(Boolean))];
    setDisplayableRegions(regions.sort());
  } else {
    setDisplayableRegions([]);
  }
}, [_allSectionsForOrg]);

// Derive displayableNetworks based on _allNetworksForOrg and selectedRegion
useEffect(() => {
  if (selectedOrganizationId) {
    if (selectedRegion && _allSectionsForOrg.length > 0) {
      const networkIdsInRegion = [
        ...new Set(
          _allSectionsForOrg
            .filter(section => section.region === selectedRegion)
            .map(section => section.network_id)
        ),
      ];
      setDisplayableNetworks(
        _allNetworksForOrg.filter(network => networkIdsInRegion.includes(network.id))
      );
    } else {
      // If no region is selected, show all networks for the organization
      setDisplayableNetworks(_allNetworksForOrg);
    }
  } else {
    setDisplayableNetworks([]);
  }
}, [_allNetworksForOrg, _allSectionsForOrg, selectedRegion, selectedOrganizationId]);

// Derive displayableSections based on _allSectionsForOrg, selectedNetworkId, and selectedRegion
useEffect(() => {
  let filteredSections = _allSectionsForOrg;

  if (selectedRegion) {
    filteredSections = filteredSections.filter(
      (section) => section.region === selectedRegion
    );
  }

  if (selectedNetworkId) {
    filteredSections = filteredSections.filter(
      (section) => section.network_id === selectedNetworkId
    );
  }
  
  setDisplayableSections(filteredSections);
}, [_allSectionsForOrg, selectedNetworkId, selectedRegion]);

// Fetch Types when Category changes
useEffect(() => {
  const fetchTypes = async () => {
    try {
      if (selectedCategoryId) {
        // Fetch types filtered by category
        const response = await classificationApi.getTypes({ category_id: selectedCategoryId });
        setTypesList(response.data);
      } else {
        // Fetch all types
        const response = await classificationApi.getTypes();
        setTypesList(response.data);
      }
    } catch (error) {
      console.error('Error fetching types:', error);
      setTypesList([]);
    }
  };
  
  fetchTypes();
  // Reset is handled in handleCategoryChange
}, [selectedCategoryId]);

// Fetch Subtypes when Type changes (not Category)
useEffect(() => {
  const fetchSubtypes = async () => {
    try {
      if (selectedTypeId) {
        // Fetch subtypes filtered by type
        const response = await classificationApi.getSubtypes({ type_id: selectedTypeId });
        setSubtypesList(response.data);
      } else {
        // Fetch all subtypes
        const response = await classificationApi.getSubtypes();
        setSubtypesList(response.data);
      }
    } catch (error) {
      console.error('Error fetching subtypes:', error);
      setSubtypesList([]);
    }
  };
  
  fetchSubtypes();
  // Reset is handled in handleTypeChange
}, [selectedTypeId]);

const collectCurrentFilters = () => {
  return {
    organization_id: selectedOrganizationId,
    network_id: selectedNetworkId,
    section_id: selectedSectionId,
    category_id: selectedCategoryId,
    type_id: selectedTypeId,
    subtype_id: selectedSubtypeId,
    searchTerm: searchTerm,
    region: selectedRegion,
    status: selectedStatus
  };
};

  return (
    <>
      {/* Filter Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 10,
        }}
      >
        <Tooltip
          title={isFilterPanelVisible ? "Hide Filters" : "Show Filters"}
          placement="right"
        >
          <Badge
            variant="dot"
            color="error"
            invisible={!(selectedOrganizationId || selectedNetworkId || selectedSectionId || selectedCategoryId || selectedTypeId || selectedSubtypeId || searchTerm || selectedStatus)}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                top: 6,
                right: 6,
              }
            }}
          >
            <IconButton
              onClick={toggleFilterPanel}
              size="large"
              sx={{
                backgroundColor: isFilterPanelVisible
                  ? alpha(theme.palette.primary.main, 0.9)
                  : alpha(theme.palette.background.paper, 0.85),
                color: isFilterPanelVisible ? 'white' : 'inherit',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isFilterPanelVisible
                    ? alpha(theme.palette.primary.main, 1)
                    : alpha(theme.palette.background.paper, 0.95),
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                },
              }}
            >
              <FilterListIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Badge>
        </Tooltip>
      </Box>

      {/* Render the Filter Panel - only when visible */}
      {isFilterPanelVisible && (
        <FilterPanel
          organizations={organizationsList}
          networks={displayableNetworks}
          sections={displayableSections}
          regions={displayableRegions}
          categories={categoriesList}
          types={typesList}
          subtypes={subtypesList}
          assets={assets}
          selectedOrganizationId={selectedOrganizationId}
          selectedNetworkId={selectedNetworkId}
          selectedSectionId={selectedSectionId}
          selectedRegion={selectedRegion}
          selectedCategoryId={selectedCategoryId}
          selectedTypeId={selectedTypeId}
          selectedSubtypeId={selectedSubtypeId}
          selectedStatus={selectedStatus}
          onOrganizationChange={handleOrganizationChange}
          onNetworkChange={handleNetworkChange}
          onSectionChange={handleSectionChange}
          onRegionChange={handleRegionChange}
          onCategoryChange={handleCategoryChange}
          onTypeChange={handleTypeChange}
          onSubtypeChange={handleSubtypeChange}
          onStatusChange={handleStatusChange}
          onResetFilters={handleResetFilters}
          onSearchTermChange={setSearchTerm}
          displayedCount={displayedCount}
          totalCount={totalAssetCount}
          showRoadLines={showRoadLines}
          setShowRoadLines={setShowRoadLines}
          showIoTSensors={showIoTSensors}
          setShowIoTSensors={setShowIoTSensors}
          showSgTebuLayers={showSgTebuLayers}
          setShowSgTebuLayers={setShowSgTebuLayers}
          showAssetFootprints={showAssetFootprints}
          setShowAssetFootprints={setShowAssetFootprints}
          searchTerm={searchTerm}
        />
      )}
    </>
  );
};

export default FilterManager;